<template>
  <div>
    <div class="fixed inset-0 bg-blue-800"></div>

    <div class="navbar-top">
      <div class="header-wrapper">
        <div class="flex justify-center items-center">
          <font-awesome-icon icon="arrow-left"
                             class="text-white text-2xl"
                             @click="goBack"></font-awesome-icon>
        </div>
        <div class="flex justify-center items-center">
          <p class="text-lg text-white font-semibold">Форматирование текста</p>
        </div>
        <div class="flex justify-center items-center">
          <!-- Empty div for layout consistency -->
        </div>
      </div>
    </div>

    <div class="wrapper has-navbar-top fixed inset-0">
      <div class="flex flex-col justify-start align-top p-6 md:px-3 h-full">
        <div class="space-y-6">
          <!-- Input textarea -->
          <div class="flex-1 flex flex-col">
            <label class="text-white font-light mb-2">Введите или вставьте текст:</label>
            <textarea
              v-model="inputText"
              ref="textInput"
              class="h-48 p-3 rounded resize-none md:h-64"
              placeholder="Введите текст для форматирования..."
              @focus="setActiveTextarea('input')"
              @click="updateCursorPosition('input')"
              @keyup="updateCursorPosition('input')"
            ></textarea>
          </div>

          <!-- Output textarea -->
          <div class="flex-1 flex flex-col">
            <label class="text-white font-light mb-2">Отформатированный текст:</label>
            <textarea
              v-model="outputText"
              ref="textOutput"
              class="h-48 p-3 rounded resize-none md:h-64"
              placeholder="Отформатированный текст появится здесь..."
              @focus="setActiveTextarea('output')"
              @click="updateCursorPosition('output')"
              @keyup="updateCursorPosition('output')"
            ></textarea>
          </div>

          <!-- Special characters keyboard -->
          <div class="">

            <!-- Superscripts -->
            <div class="mb-4">
              <p class="text-white mb-2">Надстрочные знаки:</p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="char in specialChars.superscript"
                  :key="char"
                  class="keyboard-key"
                  @click="insertCharacter(char)"
                >
                  X{{ char }}
                </button>
              </div>
            </div>

            <!-- Subscripts -->
            <div class="mb-4">
              <p class="text-white mb-2">Подстрочные знаки:</p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="char in specialChars.subscript"
                  :key="char"
                  class="keyboard-key"
                  @click="insertCharacter(char)"
                >
                  X{{ char }}
                </button>
              </div>
            </div>

            <!-- Arrows -->
            <div class="mb-4">
              <p class="text-white mb-2">Стрелки:</p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="char in specialChars.arrows"
                  :key="char"
                  class="keyboard-key text-xl"
                  @click="insertCharacter(char)"
                >
                  {{ char }}
                </button>
              </div>
            </div>

            <!-- Abbreviations -->
            <div class="mb-4">
              <p class="text-white mb-2">Сокращения:</p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="char in specialChars.abbreviations"
                  :key="char"
                  class="keyboard-key text-xs"
                  @click="insertCharacter(char)"
                >
                  {{ char }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Format button -->
        <div class="mt-auto">
          <button
            class="w-full py-3 bg-blue-600 hover:bg-blue-500 rounded font-bold text-white"
            @click="formatText"
          >
            Форматировать
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PageFormat',
  data() {
    return {
      inputText: '',
      outputText: '',
      cursorPosition: 0,
      activeTextarea: 'input',
      specialChars: {
        superscript: ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '⁰', '⁺', '⁻'],
        subscript: ['₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉', '₀'],
        arrows: ['→', '↓', '↑', '↛', '⇄', '[t]→', '[⚡️]→', '[]→', '⇒'],
        abbreviations: ['(р.)', '(конц.)', '(тв.)'],
      },
    };
  },
  methods: {
    goBack() {
      this.$router.push('/');
    },
    setActiveTextarea(name) {
      this.activeTextarea = name;
    },
    insertCharacter(char) {
      const isInput = this.activeTextarea === 'input';
      const textarea = isInput ? this.$refs.textInput : this.$refs.textOutput;
      const text = isInput ? this.inputText : this.outputText;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Insert character at cursor position
      const newText = text.substring(0, start) + char + text.substring(end);
      if (isInput) {
        this.inputText = newText;
      } else {
        this.outputText = newText;
      }

      // Restore cursor position after character insertion - advance by length of inserted text
      this.$nextTick(() => {
        const newCursorPosition = start + char.length;
        textarea.selectionStart = newCursorPosition;
        textarea.selectionEnd = newCursorPosition;
        textarea.focus();
      });
    },
    updateCursorPosition(name) {
      this.activeTextarea = name;
      const textarea = name === 'input' ? this.$refs.textInput : this.$refs.textOutput;
      this.cursorPosition = textarea.selectionStart;
    },
    formatText() {
      // Helper functions for character mappings
      const toSubscript = (digit) => {
        const subscriptMap = {
          0: '₀',
          1: '₁',
          2: '₂',
          3: '₃',
          4: '₄',
          5: '₅',
          6: '₆',
          7: '₇',
          8: '₈',
          9: '₉',
        };
        return subscriptMap[digit] || digit;
      };

      const toSuperscript = (digit) => {
        const superscriptMap = {
          0: '⁰',
          1: '¹',
          2: '²',
          3: '³',
          4: '⁴',
          5: '⁵',
          6: '⁶',
          7: '⁷',
          8: '⁸',
          9: '⁹',
        };
        return superscriptMap[digit] || digit;
      };

      this.outputText = this.inputText.split('\n').map((line) => {
        let text = line;

        // Basic typographic rules
        text = text.replace(/^\s+/g, '');
        text = text.replace(/\s{2,}/g, '');
        text = text.replace(/^-([^ ])/g, '- $1');
        text = text.replace(/(\s)-(\s)/g, '$1—$2');

        // Arrow conversion rules
        text = text.replace(/->/g, '→');
        text = text.replace(/=>/g, '⇒');
        text = text.replace(/=/g, '→');
        text = text.replace(/\^/g, '↑');
        text = text.replace(/&/g, '↓');

        // Electron configuration rules
        text = text.replace(/(\d)([spdf])(\d+)(\s)/g, (match, digit1, orbital, digit2, space2) => {
          const superscriptDigit2 = digit2.split('').map(d => toSuperscript(d)).join('');
          return digit1 + orbital + superscriptDigit2 + space2;
        });

        // Sign conversion rules
        text = text.replace(/([A-Za-z0-9])([+-])/g, (match, leadingChars, sign) => {
          const superscriptSign = sign === '+' ? '⁺' : '⁻';
          return leadingChars + superscriptSign;
        });

        // Non-single ion charge rules
        text = text.replace(/(\d+)([⁺⁻])(\d*)/g, (match, leadingDigits, sign, trailingDigits) => {
          const superscriptLeadingDigits = leadingDigits.split('').map(d => toSuperscript(d)).join('');
          const subscriptTrailingDigits = trailingDigits.split('').map(d => toSubscript(d)).join('');
          return superscriptLeadingDigits + sign + subscriptTrailingDigits;
        });

        // Oxidation state rules
        text = text.replace(/([⁺⁻])(\d)/g, (match, sign, trailingDigit) => {
          const superscriptTrailingDigit = toSuperscript(trailingDigit);
          return sign + superscriptTrailingDigit;
        });

        // Index digits rules
        text = text.replace(/([A-Za-z)])(\d+)/g, (match, prefix, digits) => {
          const subscriptDigits = digits.split('').map(d => toSubscript(d)).join('');
          return prefix + subscriptDigits;
        });

        return text;
      }).join('\n');
    },
  },
};
</script>

<style scoped>
textarea {
  font-size: 16px;
  line-height: 1.5;
}

.keyboard-key {
  @apply bg-blue-600 text-white font-semibold h-12 px-3 rounded text-lg;
  min-width: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
}

.keyboard-key:hover {
  @apply bg-blue-500;
}

.keyboard-key:active {
  @apply bg-blue-400;
}
</style>
